// component-compare-manager.js

/* ============================== CONSTANTS ================================== */
const STORAGE_KEY           = "compare:handles";
const THUMB_CACHE_KEY       = "compare:thumbs";
const TITLES_CACHE_KEY      = "compare:titles";
// ===== Shared localStorage helpers =====
const ACTIVE_COLLECTION_KEY = "compare:active_collection";
const WORKING_KEY           = "compare:handles:working";
// const handlesKeyFor = (h) => `compare:handles:${String(h||"").toLowerCase()}`;
// const readJSON  = (k, fb) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : fb; } catch { return fb; } };
const writeJSON = (k, v)   => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };

const COMPARE_PAGE_URL      = "/pages/comparison-1"; // adjust if needed

const DEFAULT_COMPARE_COLLECTIONS = [
  { title: "Base Layers",  handle: "base-layers"  },
  { title: "Snow Jackets", handle: "snow-jackets" },
  { title: "Snow Pants",   handle: "snow-pants"   },
];

/* ============================ SMALL HELPERS ================================= */
const isCollectionPath = () => /\/collections\/[^/?#]+/i.test(location.pathname);
const t = (s) => String(s ?? "").replace(/[<>]/g, "");

const unsizeImage = (u) =>
  u ? u.replace(/(_\d+x\d*|_\d+x)(?=\.[a-z]{3,4}(\?|$))/i, "") : u;

const sizeImage = (url, size = "190x") =>
  url
    ? (/_\d+x(?:\d+)?\./i.test(url) ? url : url.replace(/(\.[a-z]{3,4})(\?.*)?$/i, `_${size}$1$2`))
    : url;

const readTitles = () => { try { return JSON.parse(localStorage.getItem(TITLES_CACHE_KEY)) || {}; } catch { return {}; } };
const writeTitles = (m) => { try { localStorage.setItem(TITLES_CACHE_KEY, JSON.stringify(m)); } catch {} };

const readThumbs = () => { try { return JSON.parse(localStorage.getItem(THUMB_CACHE_KEY)) || {}; } catch { return {}; } };
const writeThumbs = (m) => { try { localStorage.setItem(THUMB_CACHE_KEY, JSON.stringify(m)); } catch {} };

/* one-time: normalize thumb cache to store unsized bases */
(() => {
  try {
    const map = readThumbs();
    let changed = false;
    for (const k in map) {
      const u = unsizeImage(map[k]);
      if (u && u !== map[k]) { map[k] = u; changed = true; }
    }
    if (changed) writeThumbs(map);
  } catch {}
})();

/* ================================ STORAGE =================================== */
export function readHandles() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : Array.isArray(parsed?.handles) ? parsed.handles : [];
  } catch { return []; }
}

function writeHandles(arr) {
  const next = Array.from(new Set(arr)).slice(0, 8);
  localStorage.setItem("compare:handles", JSON.stringify(next)); // PLP tray
  const m = location.pathname.match(/\/collections\/([^/?#]+)/i);
  if (m && m[1]) localStorage.setItem(`compare:handles:${m[1].toLowerCase()}`, JSON.stringify(next)); // per-collection sticky
  window.dispatchEvent(new CustomEvent("compare_change", { detail: { handles: next } }));
  return next;
}

function removeHandle(handle) { writeHandles(readHandles().filter(h => h !== handle)); }
function addHandle(handle) {
  const now = readHandles();
  if (!now.includes(handle)) {
    writeHandles([handle, ...now]);
  }
}

/* persist active collection context */
function setActiveCollectionFromLocation() {
  try {
    const m = location.pathname.match(/\/collections\/([^/?#]+)/i);
    if (!m) return;
    const handle = m[1];
    const known  = DEFAULT_COMPARE_COLLECTIONS.find(c => c.handle === handle);
    const title  = known ? known.title : handle.replace(/-/g, " ").replace(/\b\w/g, s => s.toUpperCase());
    localStorage.setItem(ACTIVE_COLLECTION_KEY, JSON.stringify({ handle, title }));
  } catch {}
}

/* ============================== WEB COMPONENT =============================== */
class CompareManager extends HTMLElement {
  constructor() {
    super();
    this.expanded = false;
    this._showTotalProducts = this.dataset?.showTotalProducts === "true" || false;

    this._onToggle = this._onToggle.bind(this);
    this._onKey    = this._onKey.bind(this);

    this._onAdded = (e) => {
      const { handle: h, image, title } = e.detail || {};
      if (image) { const map = readThumbs(); map[h] = unsizeImage(image); writeThumbs(map); }
      if (title) { const titles = readTitles(); titles[h] = String(title); writeTitles(titles); }
      addHandle(h);
      this._renderItems();
      this._show();
      this._updateVisibility(true);
      this._dl("add", h);
    };

    this._onRemoved = (e) => {
      removeHandle(e.detail.handle);
      this._renderItems();
      this._dl("remove", e.detail.handle);
      this._updateVisibility(false);
    };

    this._onCleared = () => {
      const prev = readHandles();
      writeHandles([]);

      // also clear the sticky list for the current collection only
      try {
        const m = location.pathname.match(/\/collections\/([^/?#]+)/i);
        if (m && m[1]) localStorage.removeItem(`compare:handles:${m[1].toLowerCase()}`);

      } catch {}

      this._renderItems();
      this._updateVisibility(false);
      this._dl("clear", prev);
    };


    this._onChange = () => this._renderItems();
  }

  connectedCallback() {

    try { localStorage.setItem("compare:scope", "collection"); } catch {}

    const allowKeep = /(?:^|[?&])cmp_keep=1(?:&|$)/.test(location.search);
    try {
      if (isCollectionPath() && !allowKeep) {
        const scope = localStorage.getItem("compare:scope");
        if (scope !== "collection") {
          localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
          window.dispatchEvent(new CustomEvent("compare_change", { detail: { handles: [] } }));
        }
      }
    } catch {}


    this._render();
    this._mq = window.matchMedia("(max-width: 767px)");
    const _onBpChange = () => { this._setNoteText(); this._renderItems(); };
    if (this._mq.addEventListener) this._mq.addEventListener("change", _onBpChange);
    else this._mq.addListener(_onBpChange);

    this._setNoteText();


    window.addEventListener("compare_product_added", this._onAdded);
    window.addEventListener("compare_product_removed", this._onRemoved);
    window.addEventListener("compare_cleared", this._onCleared);
    window.addEventListener("compare_change", this._onChange);
    window.addEventListener("storage", this._onChange);

    // Hydrate PLP tray from this collection's sticky list (if any)
    try {
      const m = location.pathname.match(/\/collections\/([^/?#]+)/i);
      const handle = m && m[1] ? m[1].toLowerCase() : "";
      if (handle) {
        // keep ACTIVE_COLLECTION_KEY in sync
        setActiveCollectionFromLocation();

        const raw = localStorage.getItem(`compare:handles:${handle}`);
        const arr = raw ? JSON.parse(raw) : [];
        const next = Array.isArray(arr) ? arr : [];

        // write to the PLP modal's working key so UI shows those actives
        localStorage.setItem("compare:handles", JSON.stringify(next));
        // notify listeners so the tray paints immediately
        window.dispatchEvent(new CustomEvent("compare_change", { detail: { handles: next } }));
      }
    } catch {}


    setActiveCollectionFromLocation();

    // paint immediately (no idle delay so the panel doesn't show old items)
    this._renderItems();
  }

  disconnectedCallback() {
    window.removeEventListener("compare_product_added", this._onAdded);
    window.removeEventListener("compare_product_removed", this._onRemoved);
    window.removeEventListener("compare_cleared", this._onCleared);
    window.removeEventListener("compare_change", this._onChange);
    window.removeEventListener("storage", this._onChange);
    if (this._mq) {
      if (this._mq.removeEventListener) this._mq.removeEventListener("change", () => {});
      else this._mq.removeListener(() => {});
    }

  }

  async _ensureThumbs(handles) {
    if (this._thumbsInFlight) return;
    const thumbs = readThumbs();
    const missing = handles.filter(h => !thumbs[h]);
    if (!missing.length) return;

    this._thumbsInFlight = true;
    try {
      const perHandle = missing.map((h, i) => `
        h${i}: product(handle: "${h}") {
          handle
          featuredImage { url(transform: { maxWidth: 800 }) }
          images(first: 1) { edges { node { url(transform: { maxWidth: 800 }) } } }
        }
      `).join("\n");
      const q = `query CompareThumbs { ${perHandle} }`;
      const data = await window.__VPV?.storefront?.q?.(q);

      if (data) {
        missing.forEach((h, i) => {
          const node = data[`h${i}`];
          const url = node?.featuredImage?.url || node?.images?.edges?.[0]?.node?.url || "";
          if (url) thumbs[h] = unsizeImage(url);
        });
        writeThumbs(thumbs);
      }
    } catch {} finally {
      this._thumbsInFlight = false;
      if (this.isConnected) this._renderItems();
    }
  }

  _limit() {
    // Mobile = 2, Desktop = 3
    return window.matchMedia("(max-width: 767px)").matches ? 2 : 3;
  }

  _setNoteText() {
    const note = this.querySelector("[data-note]");
    if (!note) return;
    if (this._limit() === 2) {
      note.textContent = "You can only compare a maximum of 2 products.";
    } else {
      note.textContent = "You can only compare a maximum of 3 products. Drag to reorder or remove additional products.";
    }
  }

  _render() {
    this.innerHTML = `
      <div class="compare-manager__wrap">
        <button class="btn btn-white compare-manager__toggle" aria-expanded="false" aria-controls="compare-manager__panel">
          <span>Compare</span>
          ${this._showTotalProducts ? '<span data-count>(0)</span>' : ''}
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.38053 11.1602L8.0906 4.86614L14.7139 11.1602" stroke="currentColor" stroke-width="2.5"/></svg>
        </button>
        <div id="compare-manager__panel" class="compare-manager__panel" aria-hidden="true" role="dialog" aria-label="Compare manager">
          <div class="compare-manager__note" hidden data-note>
            You can only compare a maximum of 3 products. Drag to reorder or remove additional products.
          </div>
          <div class="compare-manager__note" hidden data-note></div>
          <div class="compare-manager__content">
            <div class="compare-manager__list" role="list" tabindex="-1"></div>
            <div class="actions">
              <button class="btn__redirect" data-go>Compare Products</button>
              <button class="btn__clear" data-clear>Remove all</button>
            </div>
          </div>
        </div>
      </div>
    `;

    this.querySelector(".compare-manager__toggle")?.addEventListener("click", this._onToggle);

    this.querySelector("[data-clear]")?.addEventListener("click", () => {
      const prev = readHandles();
      writeHandles([]);
      window.dispatchEvent(new CustomEvent("compare_cleared", { detail: { previous: prev } }));
    });

    this.querySelector("[data-go]")?.addEventListener("click", () => {
      const m = location.pathname.match(/\/collections\/([^/?#]+)/i);
      const handle = m ? m[1] : (window.__plpCollectionHandle || "");
      const title  = handle.replace(/-/g, " ").replace(/\b\w/g, s => s.toUpperCase());

      writeJSON(ACTIVE_COLLECTION_KEY, { handle, title });

      const full = readHandles();
      writeJSON(WORKING_KEY, full);
      localStorage.setItem("compare:last_source", "plp");
      try { sessionStorage.setItem("compare:from", "plp"); } catch {}
      const url = `/pages/comparison-1?refCol=${encodeURIComponent(handle)}`;
      location.assign(url);

    });



    this.addEventListener("keydown", this._onKey);
  }

  _updateVisibility(force = false) {
    const count = readHandles().length;
    if (count > 0) {
      this.classList.remove("hidden");
      if (force) this._show();
    } else {
      this.classList.add("hidden");
    }
  }

  _renderItems() {
    const handles = readHandles();
    const list  = this.querySelector(".compare-manager__list");
    const count = this.querySelector("[data-count]");
    const note  = this.querySelector("[data-note]");
    if (!list) return;

    const limit = this._limit();

    list.innerHTML = "";
    if (count) count.textContent = `(${handles.length})`;
    if (note)  note.hidden = handles.length <= limit;

    const frag   = document.createDocumentFragment();
    const thumbs = readThumbs();

    const FALLBACK_IMG =
      this.dataset?.fallback ||
      window.__NO_IMAGE__ ||
      "https://cdn.shopify.com/shopifycloud/web/assets/v1/no-image-50x50-b5a6b8b0c6.png";

    handles.forEach((h, i) => {
      const li = document.createElement("div");
      li.className = "compare-manager__item" + (i >= limit ? " is_disabled" : "");
      li.setAttribute("role", "listitem");
      li.draggable = true;
      li.dataset.handle = h;

      const raw = thumbs[h];
      const src = sizeImage(unsizeImage(raw || ""), "190x") || FALLBACK_IMG;

      li.innerHTML = `
        <img alt="${t(h)}" loading="lazy" decoding="async" src="${src}">
        <button class="manager__item__remove" aria-label="Remove ${t(h)}">
          <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.3818 18.7138C7.57036 6.90238 5.66797 5 5.66797 5" stroke="currentColor" stroke-width="2.5"/>
            <path d="M5.66699 18.7143C17.4781 6.90244 19.3805 5 19.3805 5" stroke="currentColor" stroke-width="2.5"/>
          </svg>
        </button>
      `;

      li.querySelector(".manager__item__remove")?.addEventListener("click", () => {
        removeHandle(h);
        window.dispatchEvent(new CustomEvent("compare_product_removed", { detail: { handle: h } }));
      });

      li.addEventListener("dragstart", (e) => {
        e.dataTransfer?.setData("text/plain", h);
        e.dataTransfer?.setDragImage(li, 32, 32);
      });
      li.addEventListener("dragover", (e) => { e.preventDefault(); li.style.outline = "2px dashed #999"; });
      li.addEventListener("dragleave", () => { li.style.outline = ""; });
      li.addEventListener("drop", (e) => {
        e.preventDefault();
        li.style.outline = "";
        const from = e.dataTransfer?.getData("text/plain");
        if (!from || from === h) return;
        const arr = readHandles();
        const fromIdx = arr.indexOf(from);
        const toIdx   = arr.indexOf(h);
        if (fromIdx >= 0 && toIdx >= 0) {
          arr.splice(toIdx, 0, arr.splice(fromIdx, 1)[0]);
          writeHandles(arr);
          this._renderItems();
        }
      });

      frag.appendChild(li);
    });

    list.appendChild(frag);

    // ensure we have thumbs for everything that’s shown
    this._ensureThumbs(handles);

    // hide the tray if empty
    this._updateVisibility(false);
  }

  _show() {
    const panel  = this.querySelector("#compare-manager__panel");
    const toggle = this.querySelector(".compare-manager__toggle");
    if (!panel || !toggle) return;
    this.expanded = true;
    panel.setAttribute("aria-hidden", "false");
    toggle.setAttribute("aria-expanded", "true");
    panel.querySelector(".compare-manager__list")?.focus();
  }

  _onToggle() {
    const panel  = this.querySelector("#compare-manager__panel");
    const toggle = this.querySelector(".compare-manager__toggle");
    if (!panel || !toggle) return;
    this.expanded = !this.expanded;
    panel.setAttribute("aria-hidden", this.expanded ? "false" : "true");
    toggle.setAttribute("aria-expanded", String(this.expanded));
    if (this.expanded) panel.querySelector(".compare-manager__list")?.focus();
  }

  _onKey(e) { if (e.key === "Escape" && this.expanded) this._onToggle(); }

  _dl(action, payload) {
    (window.dataLayer = window.dataLayer || []).push({
      event: "compare_" + action,
      compare: { handles: readHandles(), payload },
    });
  }
}

customElements.define("compare-manager", CompareManager);

/* ===================== "COMPARE {COLLECTION}" BUTTON ======================== */
function addCompareButtonOnCollectionTitle() {
  if (!isCollectionPath()) return;

  // try a bunch of common header selectors
  const selectors = [
    '.collections-list__title-wrapper .title',
    '.collection-hero__title h1',
    '.collection-hero__title',
    '.collection-hero h1',
    '.collection__title h1',
    '.collection__title',
    '.page-title h1',
    'h1.page-title',
    '.section-header__title',
    'h1.title'
  ];
  let h1 = null;
  for (const sel of selectors) { h1 = document.querySelector(sel); if (h1) break; }
  if (!h1) return;

  // prevent duplicates
  if (h1.parentElement?.querySelector?.(".btn--compare-collection")) return;

  const m = location.pathname.match(/\/collections\/([^/?#]+)/i);
  const handle = m ? m[1] : "";
  const titleText = (h1.textContent || handle).replace(/\s+/g, " ").trim();

  const btn = document.createElement("a");
  btn.className = "btn btn--compare-collection";
  btn.href = `${COMPARE_PAGE_URL}?refCol=${encodeURIComponent(handle)}`;
  // btn.href = `${COMPARE_PAGE_URL}?from=plp&refCol=${encodeURIComponent(handle)}`;
  btn.textContent = `COMPARE ${titleText.toUpperCase()}`;
  btn.addEventListener("click", () => {
    try { localStorage.setItem(ACTIVE_COLLECTION_KEY, JSON.stringify({ handle, title: titleText })); } catch {}
  });

  const wrap = document.createElement("div");
  wrap.className = "collection-title-row";
  h1.parentNode.insertBefore(wrap, h1);
  wrap.appendChild(h1);
  wrap.appendChild(btn);
}

document.addEventListener("DOMContentLoaded", addCompareButtonOnCollectionTitle);
